/*
 * TASK PRIORITY CONTROL
 *
 * Provides the shared three-option priority selector for task popups.
 *
 * Recent changes:
 * - Added a responsive segmented treatment with semantic radio inputs and priority dots.
 */

import { TaskPriority } from '../../../models/Task';
import { THEME_CLASSES } from '../../../constants/themeConstants';

interface TaskPriorityControlProps {
  idPrefix: string;
  value: TaskPriority;
  onChange: (value: TaskPriority) => void;
  disabled?: boolean;
}

const OPTIONS: Array<{ value: TaskPriority; dot: string }> = [
  { value: 'Low', dot: 'bg-sky-400' },
  { value: 'Medium', dot: 'bg-amber-500' },
  { value: 'High', dot: 'bg-rose-500' },
];

export default function TaskPriorityControl({
  idPrefix,
  value,
  onChange,
  disabled = false,
}: TaskPriorityControlProps) {
  return (
    <fieldset disabled={disabled}>
      <legend className={`mb-2 text-xs font-semibold uppercase tracking-[0.12em] ${THEME_CLASSES.text.secondary}`}>
        Priority
      </legend>
      <div className="grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-900">
        {OPTIONS.map((option) => (
          <label
            key={option.value}
            className={`relative flex min-w-0 cursor-pointer items-center justify-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors focus-within:ring-2 focus-within:ring-blue-500 ${
              value === option.value
                ? 'bg-white font-medium text-slate-900 shadow-sm dark:bg-slate-800 dark:text-slate-50'
                : `${THEME_CLASSES.text.secondary} hover:text-slate-900 dark:hover:text-slate-100`
            } ${disabled ? 'cursor-not-allowed opacity-70' : ''}`}
          >
            <input
              id={`${idPrefix}-${option.value.toLowerCase()}`}
              type="radio"
              name={`${idPrefix}-priority`}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            <span className={`h-2 w-2 shrink-0 rounded-full ${option.dot}`} aria-hidden="true" />
            <span className="truncate">{option.value}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
